// ---------------------------------------------------------------------------
// Queue Retry Delay Calculator
// ---------------------------------------------------------------------------
// Exponential backoff with crypto jitter for Cloudflare Queue consumers.
// Prevents thundering herd when batch failures cause synchronized retries.
//
// Pattern extracted from event-webhooks (gold standard) and standardized
// across all 9 queue consumers. See EFFICIENCY-REMEDIATION-PLAN.md P01.

const DEFAULT_BASE_MS = 1_000;
const DEFAULT_MAX_SECONDS = 300;

export interface RetryDelayOptions {
  /** Base delay in milliseconds. Default: 1000 (1 second). */
  baseMs?: number;
  /** Maximum delay in seconds. Default: 300 (5 minutes). */
  maxSeconds?: number;
}

/**
 * Core math for exponential backoff with crypto jitter.
 *
 * Formula: min(baseMs * 2^(attempt-1) + jitter, maxSeconds * 1000)
 * Jitter: [0, baseMs) using crypto.getRandomValues for non-determinism.
 *
 * @returns Total delay in milliseconds (raw, no rounding).
 */
function computeDelayMs(attempt: number, opts?: RetryDelayOptions): number {
  const safeAttempt = Math.max(1, attempt);
  const base = opts?.baseMs ?? DEFAULT_BASE_MS;
  const max = opts?.maxSeconds ?? DEFAULT_MAX_SECONDS;
  const backoffMs = base * Math.pow(2, safeAttempt - 1);
  const jitterBytes = crypto.getRandomValues(new Uint8Array(2));
  const highByte = jitterBytes[0] ?? 0;
  const lowByte = jitterBytes[1] ?? 0;
  const jitterMs = (((highByte << 8) | lowByte) / 65_536) * base;
  return Math.min(backoffMs + jitterMs, max * 1_000);
}

/**
 * Calculate retry delay with exponential backoff + crypto jitter.
 *
 * @param attempt - CF Queue message attempt number (starts at 1)
 * @param opts - Optional base delay and max cap overrides
 * @returns Delay in whole seconds (Math.ceil — preserves >=1s guarantee)
 */
export function getRetryDelaySeconds(
  attempt: number,
  opts?: RetryDelayOptions,
): number {
  return Math.ceil(computeDelayMs(attempt, opts) / 1_000);
}

/**
 * Same formula as {@link getRetryDelaySeconds} but returns milliseconds
 * directly, preserving sub-second jitter precision.
 *
 * Use this when feeding `setTimeout`, `Promise` delays, or any API that
 * accepts millisecond granularity. The seconds variant exists for
 * Cloudflare Queue `delaySeconds` which only accepts whole seconds.
 *
 * @param attempt - CF Queue message attempt number (starts at 1)
 * @param opts - Optional base delay and max cap overrides
 * @returns Delay in milliseconds (no rounding)
 */
export function getRetryDelayMs(
  attempt: number,
  opts?: RetryDelayOptions,
): number {
  return computeDelayMs(attempt, opts);
}
